import type { components } from '../../api/generated/schema'

type Schema = components['schemas']

/**
 * The composer's vocabulary — for **both** composers.
 *
 * A prompt is a list of blocks in a meaningful order, and that is true whether the thing being
 * drawn is a person or a place. So one set of types serves the portrait workbench and the
 * location workbench, parameterized by a `ComposerOwner` — exactly the move `ImageGallery` makes
 * for the two galleries, and for the same reason (`docs/web-api/phase-9-kickoff.md` §1,
 * decision 5).
 *
 * **These types are hand-written**, unlike the gallery's. The composer endpoints are new and
 * `src/api/generated/schema.d.ts` is regenerated against a running API (`npm run api:types`), so
 * until that is run these say what the contract is. They are transcribed from
 * `Contracts/Images/ComposerContracts.cs` and `Contracts/Images/LocationComposerContracts.cs`,
 * and the drift check will confirm them.
 */

/** Which composer a screen is. The path and the tool set are both properties of this. */
export type ComposerOwner =
  | { kind: 'character'; characterId: number }
  | { kind: 'location'; campaignId: number; locationId: number }

/** A location image is a depiction or a battle map. The choice selects the *system prompt*. */
export type LocationImageType = Schema['LocationImageType']

/** The API path for an owner's composer. The single place either route is written down. */
export function composerPath(owner: ComposerOwner): string {
  return owner.kind === 'character'
    ? `/characters/${owner.characterId}/portrait`
    : `/campaigns/${owner.campaignId}/locations/${owner.locationId}/images`
}

/** A stable cache key fragment for an owner. */
export function composerOwnerKey(owner: ComposerOwner): readonly (string | number)[] {
  return owner.kind === 'character'
    ? ['character', owner.characterId]
    : ['location', owner.campaignId, owner.locationId]
}

/** Whether a block contributes words or a reference picture. */
export type BlockKind = 'Text' | 'Image'

/** A picture with no id, carried inline. Base64, without the `data:` prefix. */
export interface InlineImage {
  contentType: string
  data: string
}

/** One block, exactly as the API takes it. */
export interface PromptBlock {
  kind: BlockKind
  order: number
  text?: string
  characterImageId?: number
  locationImageId?: number
  draftId?: string
  upload?: InlineImage
}

/**
 * Where an image block's picture comes from. Exactly one of these reaches the API per block.
 *
 * Both gallery kinds are here rather than one per composer, because the API accepts both from
 * either: a battle map may reference a character's art, and a portrait may reference a place.
 */
export type ImageSource =
  | { from: 'characterGallery'; characterImageId: number }
  | { from: 'locationGallery'; locationImageId: number }
  | { from: 'draft'; draftId: string }
  | { from: 'upload'; upload: InlineImage }

/**
 * A block as the screen holds it.
 *
 * The request shape plus the things a request has no use for: an `id` so React can key it and
 * the reorder controls can name it, and enough to *draw* it — a thumbnail, and a name.
 *
 * `title` is that name, and it is optional on text blocks for a reason. A block that came from
 * somewhere specific — a character's profile, a location's description, a file — has a name that
 * stays true however its content is edited, so it is stored. A block someone typed has no name
 * but its own first line, so it is derived on read and follows the edit.
 */
export type ComposerBlock =
  | { id: string; kind: 'Text'; title?: string; text: string }
  | { id: string; kind: 'Image'; title: string; preview: string; source: ImageSource }

/**
 * An unsaved generated image.
 *
 * One shape for both composers. The API's two draft DTOs differ only in whether they name a
 * `characterId` or a `locationId` — and the screen already knows which it is looking at, so
 * neither field is read here. `imageType` is location-only and absent on a portrait draft.
 */
export interface ComposerDraft {
  draftId: string
  contentType: string
  /** Base64, without the `data:` prefix. */
  data: string
  createdAt: string
  imageType?: LocationImageType
}

export type Resolution = '512' | '1K' | '2K' | '4K'
export type ThinkingLevel = 'MINIMAL' | 'HIGH'

export interface ComposerSettings {
  resolution: Resolution
  temperature: number
  thinkingLevel: ThinkingLevel
}

/** The service's own defaults, restated so a composer opens showing what it will send. */
export const DEFAULT_SETTINGS: ComposerSettings = {
  resolution: '1K',
  temperature: 1,
  thinkingLevel: 'MINIMAL',
}

/**
 * The blocks, in the API's shape.
 *
 * `order` is sent explicitly rather than left to array position — the contract has the field,
 * and a request whose meaning survives being reserialized is worth the two lines.
 */
export function toRequestBlocks(blocks: readonly ComposerBlock[]): PromptBlock[] {
  return blocks.map((block, order) => {
    if (block.kind === 'Text') {
      return { kind: 'Text' as const, order, text: block.text }
    }

    switch (block.source.from) {
      case 'characterGallery':
        return { kind: 'Image' as const, order, characterImageId: block.source.characterImageId }
      case 'locationGallery':
        return { kind: 'Image' as const, order, locationImageId: block.source.locationImageId }
      case 'draft':
        return { kind: 'Image' as const, order, draftId: block.source.draftId }
      case 'upload':
        return { kind: 'Image' as const, order, upload: block.source.upload }
    }
  })
}

/**
 * The one line a collapsed block shows.
 *
 * A prompt of six blocks, three of them a thousand words each, is unreadable expanded — so the
 * list collapses, and this is what is left. It has to answer "which one is this?" on its own:
 * "Elise's profile", "duster-reference.png", or the first thing the text says.
 */
export function blockSummary(block: ComposerBlock): string {
  if (block.kind === 'Image') return block.title
  return block.title ?? summarizeText(block.text)
}

/** The first non-empty line, short enough to sit on one row beside the controls. */
export function summarizeText(text: string, fallback = 'Text'): string {
  const line = text
    .trim()
    .split('\n')
    .find((entry) => entry.trim() !== '')
    ?.trim()

  if (line === undefined || line === '') return fallback

  return line.length <= 44 ? line : `${line.slice(0, 43).trimEnd()}…`
}

/** A draft as something an `<img>` can show. A draft has no URL; this is why it carries bytes. */
export function draftUrl(draft: ComposerDraft): string {
  return `data:${draft.contentType};base64,${draft.data}`
}

/**
 * Moves one block to a new position, returning a new list.
 *
 * The whole reordered array rather than a swap, because that is what `OrderButtons` hands back
 * and because "the prompt is this list, in this order" is the only statement worth making.
 */
export function moveBlock(
  blocks: readonly ComposerBlock[],
  from: number,
  to: number,
): ComposerBlock[] {
  if (to < 0 || to >= blocks.length || from === to) return [...blocks]

  const next = [...blocks]
  const [moved] = next.splice(from, 1)
  if (moved !== undefined) next.splice(to, 0, moved)

  return next
}

/**
 * A file, read as base64 for an inline image block.
 *
 * `FileReader` rather than `arrayBuffer()` and a hand-rolled encoder: it produces the `data:`
 * URL the preview needs and the base64 the request needs in one read, and a 5 MB image is
 * enough bytes that doing it twice is worth avoiding.
 */
export function readImageFile(file: File): Promise<{ dataUrl: string; base64: string }> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()

    reader.onerror = () => reject(new Error('That file could not be read.'))
    reader.onload = () => {
      const dataUrl = String(reader.result)
      const comma = dataUrl.indexOf(',')

      if (comma === -1) {
        reject(new Error('That file could not be read.'))
        return
      }

      resolve({ dataUrl, base64: dataUrl.slice(comma + 1) })
    }

    reader.readAsDataURL(file)
  })
}

/** Client-side identity for a block. Only ever compared, never sent. */
export function blockId(): string {
  return `block-${Math.random().toString(36).slice(2)}-${Date.now()}`
}
